import React from "react";
import { useIntl } from "react-intl";
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Dropdown,
  Input,
  makeStyles,
  Option,
  Spinner,
  MessageBar,
  tokens,
  MessageBarActions,
  MessageBarBody,
  MessageBarTitle,
  Text,
  Caption1
} from "@fluentui/react-components";
import { ArrowDownloadFilled } from "@fluentui/react-icons";
import Files, { ReactFile } from "react-files";
import { useCommand, useSelector } from "@tdp/core";
import { IAppListLro } from "../abstractions/IAppListLro";
import { createLroHooks } from "@tdp/common/lro/hooks";
import { useNavigate } from "react-router-dom";

const { useLro: useAppImportLro } = createLroHooks<IAppListLro>(
  'appImportLro',
  { newAppId: '', isAppItemUpdated: false },
);

const useStyles = makeStyles({
  container: {
    margin: "12px 0px 24px 0px",
    display: "flex",
    gap: "18px",
    flexDirection: "column",
  },
  errorBody: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    overflowY: "auto"
  }
});

export const AppListImportDialog: React.FC<
  {
    open: boolean,
    setOpen: (value: boolean) => void,
  }
> = ({ open, setOpen }) => {
  const navigate = useNavigate();
  const classes = useStyles();
  const intl = useIntl();
  const importAppPackage = useCommand((commands) => commands.appListCommands.importApp);
  const preValidateAppPackage = useCommand((commands) => commands.appValidationCommands.preValidateAppPackage);
  const resetPreValidation = useCommand((commands) => commands.appValidationCommands.resetPreValidation);
  const preValidationResult = useSelector((state) => state.appValidation.preValidation);


  const [isImporting, setIsImporting] = React.useState(false);
  const [preValidationState, setPreValidationState] = React.useState<"notStarted" | "errors" | "success" | "validating">("notStarted");
  const newAppLro = useAppImportLro([]);


  // Strings
  const titleText = intl.formatMessage({ defaultMessage: "Import an app" });
  const noIssuesText = intl.formatMessage({ defaultMessage: "No issues found" });
  const errorText = intl.formatMessage({ defaultMessage: "Your app package has errors" });
  const stillImportText = intl.formatMessage({ defaultMessage: "You can still import the app and work on it, but you must resolve these errors before you can upload or publish the app to Teams." });

  // Buttons
  const onCancel = () => {
    setOpen(false);
  }
  const onImport = () => {
    setIsImporting(true);
    const lro = newAppLro.createLro();
    importAppPackage({ lro: lro, file: zipPackage!, overwriteIfAppAlreadyExists: true })
  };

  // Hook
  React.useEffect(() => {
    if (newAppLro.lro?.state.isAppItemUpdated && newAppLro.lro?.state?.newAppId) {
      navigate(`/apps/${newAppLro.lro?.state.newAppId}/dashboard`);
    }
    if (newAppLro.lro?.status === "completed") {
      onCancel();
    }
  }, [newAppLro.lro]);

  React.useEffect(() => {
    return () => {
      onCancel();
      setIsImporting(false);
      setPackage(undefined);
      resetPreValidation();
      setPreValidationState("notStarted");
    }
  }, [])

  // File upload
  const [zipPackage, setPackage] = React.useState<File | undefined>(undefined);
  const supportFileSize = 1000000; // 1MB
  const onFileUpload = (files: ReactFile[]) => {
    try {
      const file: ReactFile = files.pop()!;
      setPackage(file as File);
    }
    catch (e) {
      // Log error
    }
  }
  const ImportButton = () => (
    <Files
      clickable
      accepts={[".zip"]}
      onChange={onFileUpload}
      maxFileSize={supportFileSize}
      minFileSize={0}
    >
      <Button
        appearance="outline"
        disabled={!!zipPackage}
        icon={preValidationState === "validating" ? <Spinner size="tiny" /> : <ArrowDownloadFilled />}
      >
        {
          preValidationState === "validating"
            ? intl.formatMessage({ defaultMessage: "Validating zipped package" })
            : intl.formatMessage({ defaultMessage: "Import zipped package" })
        }
      </Button>
    </Files>
  )
  React.useEffect(() => {
    if (zipPackage) {
      preValidateAppPackage({ appPackage: zipPackage })
      setPreValidationState("validating");
    }
  }, [zipPackage]);
  React.useEffect(() => {
    if (preValidationResult) {
      if (preValidationResult?.errors?.length === 0) {
        setPreValidationState("success");
      }
      else {
        setPreValidationState("errors");
      }
    }
  }, [preValidationResult]);


  const ErrorComponent = () => {
    return (
      <>
        {preValidationState === "success" && <Status intent="success" title={noIssuesText} />}
        {preValidationState === "errors" &&
          <div className={classes.errorBody}>
            <Caption1>{stillImportText}</Caption1>
            {preValidationResult?.errors && preValidationResult?.errors.map((error, index) => (
              <Status key={index} intent="error" title={error?.content || error?.title || ""} />
            ))
            }
          </div>
        }
      </>
    )
  }

  // Logic to render appropriate texts, this will scale with new conditions and store import
  const title = preValidationState === "errors" ? errorText : titleText;

  return (
    <Dialog open={open}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>{title}</DialogTitle>
          <DialogContent className={classes.container}>
            <ErrorComponent />
            <ImportButton />
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={() => { onCancel() }} disabled={isImporting}>
              {intl.formatMessage({ defaultMessage: "Cancel" })}
            </Button>
            <Button
              appearance="primary"
              icon={isImporting ? <Spinner size="tiny" /> : null}
              disabled={isImporting || !(preValidationState === "success" || preValidationState === "errors")}
              onClick={onImport}
            >
              {intl.formatMessage({ defaultMessage: "Import" })}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>

    </Dialog>
  );
}

const Status: React.FC<{
  intent: "warning" | "info" | "error" | "success",
  title: string,
}> = ({ title, intent }) => {
  return (
    <MessageBar intent={intent}>
      <MessageBarBody>
        <MessageBarTitle style={{ color: tokens.colorNeutralForeground1, }}>
          {title}
        </MessageBarTitle>
      </MessageBarBody>
    </MessageBar>
  )
}
